import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import EditNutritionConstraintsModal from './edit_nutrition_constraints.modal';
import {
  useGetProgrammesQuery,
  useGetSpaceQuery,
  useUpdateProgrammeMutation,
  useUpdateSpaceMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { ActivityObjectType } from '../../API';
import { reportAccountActivitySignalOperation } from '../../operations/user_profile_operations';
import { userFriendlyNameForActivitySignalSelector } from '../../features/user_id/user_id_slice';

const EditNutritionConstraintsContainer = ({ programmeId, onDismiss }) => {
  const { data: ownedProgrammes } = useGetProgrammesQuery(undefined);

  const { data: space } = useGetSpaceQuery();

  const programme = (ownedProgrammes || []).find((p) => p.id === programmeId);

  const [updateProgramme] = useUpdateProgrammeMutation();

  const [updateSpace] = useUpdateSpaceMutation();

  const [nutritionConstraints, setNutritionConstraints] = useState(
    programme?.nutritionConstraints,
  );
  const [personalisedNutritionEnabled, setPersonalisedNutritionEnabled] =
    useState(space?.personalisedNutritionEnabled);
  const [showNutritionToUsers, setShowNutritionToUsers] = useState(
    programme?.showNutritionToUsers,
  );
  const [personalisedMealScaling, setPersonalisedMealScaling] = useState(
    programme?.personalisedMealScaling,
  );

  const friendlyUsername = useSelector(
    userFriendlyNameForActivitySignalSelector,
  );

  const handleSave = async (
    newNutritionConstraints,
    newShowNutritionToUsers,
    newPersonalisedMealScaling,
    newPersonalisedNutritionEnabled,
  ) => {
    const updatedProgramme = {
      ...programme,
      nutritionConstraints: newNutritionConstraints,
      showNutritionToUsers: newShowNutritionToUsers,
      personalisedMealScaling: newPersonalisedMealScaling,
    };
    const updatedSpace = {
      ...space,
      personalisedNutritionEnabled: newPersonalisedNutritionEnabled,
    };
    await Promise.all([
      updateProgramme(updatedProgramme),
      updateSpace(updatedSpace),
    ]);
    reportAccountActivitySignalOperation({
      spaceID: space.id,
      friendlyUsername,
      lastActivityObjectType: ActivityObjectType.PROGRAMMES,
      lastActivityObjectID: programme.id,
    });
  };

  const onChangeNutritionConstraints = (newConstraints) => {
    // console.log('onChangeNutritionConstraints', JSON.stringify(newConstraints));
    setNutritionConstraints(newConstraints);
    handleSave(newConstraints, showNutritionToUsers, personalisedMealScaling);
  };

  const onChangePersonalisedNutritionEnabled = (newValue) => {
    setPersonalisedNutritionEnabled(newValue);
    handleSave(
      nutritionConstraints,
      newValue,
      personalisedMealScaling,
      newValue,
    );
  };

  const onChangeShowNutritionToUsers = (newValue) => {
    setShowNutritionToUsers(newValue);
    handleSave(
      nutritionConstraints,
      newValue,
      personalisedMealScaling,
      personalisedNutritionEnabled,
    );
  };

  const onChangePersonalisedMealScaling = (newValue) => {
    setPersonalisedMealScaling(newValue);
    handleSave(
      nutritionConstraints,
      showNutritionToUsers,
      newValue,
      personalisedNutritionEnabled,
    );
  };

  return (
    <EditNutritionConstraintsModal
      nutritionConstraints={nutritionConstraints}
      personalisedNutritionEnabled={personalisedNutritionEnabled === true}
      showNutritionToUsers={showNutritionToUsers === true}
      personalisedMealScaling={personalisedMealScaling === true}
      onChangeNutritionConstraints={onChangeNutritionConstraints}
      onChangePersonalisedNutritionEnabled={
        onChangePersonalisedNutritionEnabled
      }
      onChangeShowNutritionToUsers={onChangeShowNutritionToUsers}
      onChangePersonalisedMealScaling={onChangePersonalisedMealScaling}
      onDismiss={onDismiss}
    />
  );
};

EditNutritionConstraintsContainer.propTypes = {
  programmeId: PropTypes.string.isRequired,
  onDismiss: PropTypes.func.isRequired,
};

export default EditNutritionConstraintsContainer;
